// src/pages/gpmOrderBuilder.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCommands } from '../api/commands';
import PageHeader from '../components/PageHeader';
import Icon from '../components/Icon';

function capWords(s = '') {
  return s.replace(/(^\w|\s\w)/g, c => c.toUpperCase());
}

export default function GpmScheduleDelivery(){
  const cmd = useCommands();
  const nav = useNavigate();
  const loc = useLocation();
  const q = new URLSearchParams(loc.search);

  const preItem = q.get('item') || '';

  /* --------------------------- shared meta -------------------------- */

  const [orderedDate, setOrderedDate] = useState(
    new Date().toISOString().slice(0,10)
  );
  const [supplier, setSupplier] = useState('');
  const [orderedBy, setOrderedBy] = useState('');
  const [office, setOffice] = useState('Head');
  const [err, setErr] = useState('');

  const supplierRequired = !supplier.trim();
  const orderedByRequired = !orderedBy.trim();
  const metaInvalid = supplierRequired || orderedByRequired;

  /* ----------------------------- mode ------------------------------- */

  const [mode, setMode] = useState(preItem ? 'single' : 'single');

  /* --------------------------- inventory ---------------------------- */

  const [invRows, setInvRows] = useState([]);
  const [loadingInv, setLoadingInv] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingInv(true);
      try{
        const data = await cmd.inventory.get('main');
        if (mounted) setInvRows(Array.isArray(data) ? data : []);
      }catch{
        if (mounted) setInvRows([]);
      }finally{
        if (mounted) setLoadingInv(false);
      }
    })();
    return () => { mounted = false; };
  }, [cmd]);

  const invIndex = useMemo(() => {
    const m = new Map();
    for (const r of invRows) {
      m.set(String(r.item || '').toLowerCase(), r);
    }
    return m;
  }, [invRows]);

  const itemNames = useMemo(
    () => invRows.map(r => r.item).sort((a,b)=>a.localeCompare(b)),
    [invRows]
  );

  /* ============================== SINGLE ============================ */

  const [singleItem, setSingleItem] = useState(preItem);
  const [singleQty, setSingleQty] = useState(1);
  const [singleUnitCost, setSingleUnitCost] = useState(0);
  const singlePriceTouched = useRef(false);

  const singleMeta = useMemo(() => {
    const r = invIndex.get(String(singleItem||'').toLowerCase());
    return r ? {
      unitPrice: Number(r.unitPrice||0),
      packOf: Math.max(1, Number(r.packOf||1))
    } : { unitPrice:0, packOf:1 };
  }, [invIndex, singleItem]);

  useEffect(() => {
    if (!singlePriceTouched.current && singleMeta.unitPrice > 0) {
      setSingleUnitCost(singleMeta.unitPrice);
    }
  }, [singleMeta.unitPrice]);

  function onSingleUnitCostChange(v){
    singlePriceTouched.current = true;
    setSingleUnitCost(Math.max(0, Number(v||0)));
  }

  async function submitSingle(){
    setErr('');

    if (metaInvalid) {
      setErr('Supplier and Ordered by are required.');
      return;
    }
    if (!singleItem) {
      setErr('Select an item.');
      return;
    }

    try{
      await cmd.deliveries.create({
        item: capWords(singleItem),
        amount: Math.max(1, Number(singleQty||1)),
        unitCost: Math.max(0, Number(singleUnitCost||0)),
        supplier: capWords(supplier),
        office,
        orderedBy: capWords(orderedBy),
        orderedDate: new Date(orderedDate)
      });
      nav('/gpm/deliveries');
    }catch(e){
      setErr(e?.response?.data?.error || e?.message || 'Failed to schedule delivery');
    }
  }

  /* =============================== BULK ============================= */

  const [selName, setSelName] = useState('');
  const [qty, setQty] = useState(1);
  const [unitCost, setUnitCost] = useState(0);
  const [lines, setLines] = useState([]);

  const bulkSelMeta = useMemo(() => {
    const r = invIndex.get(String(selName||'').toLowerCase());
    return r ? {
      unitPrice: Number(r.unitPrice||0),
      packOf: Math.max(1, Number(r.packOf||1))
    } : { unitPrice:0, packOf:1 };
  }, [invIndex, selName]);

  function addLine(){
    const name = capWords(selName.trim());
    if (!name) return;

    const amount = Math.max(1, Number(qty||1));
    const inv = invIndex.get(name.toLowerCase());
    const price = Number(unitCost||0) || Number(inv?.unitPrice||0);

    setLines(prev => {
      const idx = prev.findIndex(l => l.item.toLowerCase() === name.toLowerCase());
      if (idx >= 0){
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          amount: next[idx].amount + amount,
          unitCost: price > 0 ? price : next[idx].unitCost
        };
        return next;
      }
      return [...prev, { item:name, amount, unitCost:price }];
    });

    setSelName(''); setQty(1); setUnitCost(0);
  }

  function updateQty(item, v){
    setLines(prev => prev.map(l =>
      l.item===item ? { ...l, amount:Math.max(1,Number(v||1)) } : l
    ));
  }

  function updatePrice(item, v){
    setLines(prev => prev.map(l =>
      l.item===item ? { ...l, unitCost:Math.max(0,Number(v||0)) } : l
    ));
  }

  function removeLine(item){
    setLines(prev => prev.filter(l => l.item!==item));
  }

  async function submitBulk(){
    setErr('');

    if (metaInvalid) {
      setErr('Supplier and Ordered by are required.');
      return;
    }
    if (!lines.length) {
      setErr('Add at least one item.');
      return;
    }

    try{
      for (const l of lines){
        await cmd.deliveries.create({
          item: l.item,
          amount: Number(l.amount||0),
          unitCost: Number(l.unitCost||0),
          supplier: capWords(supplier),
          office,
          orderedBy: capWords(orderedBy),
          orderedDate: new Date(orderedDate)
        });
      }
      nav('/gpm/deliveries');
    }catch(e){
      setErr(e?.response?.data?.error || e?.message || 'Failed to schedule deliveries');
    }
  }

  /* ----------------------------- render ----------------------------- */

  function packHint(meta, qty = 1) {
    if (!meta || meta.packOf <= 1) return null;
    const each = meta.unitPrice > 0 ? meta.unitPrice / meta.packOf : 0;
    const totalUnits = Number(qty || 1) * meta.packOf;
    return (
      <div className="line-hint">
        Pack of {meta.packOf}. {totalUnits} units will be added to stock.
        {meta.unitPrice > 0 && ` £${meta.unitPrice.toFixed(2)} per pack, about £${each.toFixed(2)} each.`}
      </div>
    );
  }
  // Then pass qty into it: packHint(singleMeta, singleQty)

  return (
    <div className="container">
      <PageHeader
        eyebrow="GPM"
        title="Schedule delivery"
        actions={<Link to="/gpm/deliveries" className="btn secondary">Orders</Link>}
      />

      {err && <div className="page-alerts"><div className="alert error">{err}</div></div>}

      <div className="card mb-16">
        <div className="card-head">
          <h2 className="card-title">Order details</h2>
        </div>
        <div className="form-grid cols-4">
          <label className="input-label">
            Ordered date
            <input className="input" type="date" value={orderedDate}
                   onChange={e=>setOrderedDate(e.target.value)} />
          </label>

          <label className="input-label">
            Supplier <span className="req">*</span>
            <input className="input" required
                   value={supplier}
                   onChange={e=>setSupplier(capWords(e.target.value))} />
          </label>

          <label className="input-label">
            Office
            <input className="input"
                   value={office}
                   onChange={e=>setOffice(capWords(e.target.value))} />
          </label>

          <label className="input-label">
            Ordered by <span className="req">*</span>
            <input className="input" required
                   value={orderedBy}
                   onChange={e=>setOrderedBy(capWords(e.target.value))} />
          </label>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2 className="card-title">Items</h2>
          <div className="segmented">
            <button className={mode==='single'?'seg active':'seg'} onClick={()=>setMode('single')}>Single</button>
            <button className={mode==='bulk'?'seg active':'seg'} onClick={()=>setMode('bulk')}>Bulk</button>
          </div>
        </div>

        {mode==='single' && (
          <>
            <div className="line-builder">
              <label className="input-label">Item
                <input list="gpm-items" className="input" value={singleItem}
                       onChange={e=>{setSingleItem(e.target.value);singlePriceTouched.current=false;}}
                       placeholder={loadingInv?'Loading':'Type or pick an item'} />
                <datalist id="gpm-items">
                  {itemNames.map(n=><option key={n} value={n} />)}
                </datalist>
              </label>

              <label className="input-label">Qty
                <input className="input" type="number" min={1} value={singleQty}
                       onChange={e=>setSingleQty(Math.max(1,Number(e.target.value||1)))} />
              </label>

              <label className="input-label">Unit cost
                <div className="input-wrap"><span className="prefix">£</span>
                  <input className="input" type="number" min="0" step="0.01"
                         value={singleUnitCost}
                         onChange={e=>onSingleUnitCostChange(e.target.value)} />
                </div>
              </label>

              <button className="btn" type="button"
                      disabled={metaInvalid}
                      onClick={submitSingle}>
                Schedule
              </button>
            </div>
            {packHint(singleMeta, singleQty)}
            {metaInvalid && <div className="line-hint">Supplier and ordered by are required.</div>}
          </>
        )}

        {mode==='bulk' && (
          <>
            <div className="line-builder">
              <label className="input-label">Item
                <input list="gpm-items-bulk" className="input" value={selName}
                       onChange={e=>setSelName(e.target.value)}
                       placeholder={loadingInv?'Loading':'Type or pick an item'} />
                <datalist id="gpm-items-bulk">
                  {itemNames.map(n=><option key={n} value={n} />)}
                </datalist>
              </label>

              <label className="input-label">Qty
                <input className="input" type="number" min={1} value={qty}
                       onChange={e=>setQty(Math.max(1,Number(e.target.value||1)))} />
              </label>

              <label className="input-label">Unit cost
                <div className="input-wrap"><span className="prefix">£</span>
                  <input className="input" type="number" min="0" step="0.01"
                         value={unitCost}
                         onChange={e=>setUnitCost(Number(e.target.value||0))} />
                </div>
              </label>

              <button className="btn secondary" type="button" onClick={addLine}>
                <Icon name="plus" size={15} /> Add line
              </button>
            </div>
            {packHint(bulkSelMeta)}

            {lines.length > 0 ? (
              <div className="table-wrap mt-16">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th style={{width:120}}>Qty</th>
                      <th style={{width:150}}>Unit cost</th>
                      <th className="num" style={{width:120}}>Total</th>
                      <th className="actions" style={{width:110}} />
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map(l=>(
                      <tr key={l.item}>
                        <td className="cell-strong">{l.item}</td>
                        <td>
                          <input className="input" type="number" min={1}
                                 value={l.amount}
                                 onChange={e=>updateQty(l.item,e.target.value)}
                                 style={{maxWidth:100}} />
                        </td>
                        <td>
                          <div className="input-wrap" style={{maxWidth:130}}><span className="prefix">£</span>
                            <input className="input" type="number" min="0" step="0.01"
                                   value={l.unitCost}
                                   onChange={e=>updatePrice(l.item,e.target.value)} />
                          </div>
                        </td>
                        <td className="num">£{(l.amount*l.unitCost).toFixed(2)}</td>
                        <td className="actions">
                          <button className="btn secondary small" onClick={()=>removeLine(l.item)}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty">No items added yet.</div>
            )}

            <div className="form-actions">
              <button className="btn"
                      disabled={metaInvalid || !lines.length}
                      onClick={submitBulk}>
                Schedule all
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
